import { t } from '@lingui/macro'
import { BulkPatientManagerAssignmentDTO } from '@om1/aspen-api/codegen/client/models/BulkPatientManagerAssignmentDTO'
import { BulkPatientManagerAssignmentResponseDTO } from '@om1/aspen-api/codegen/client/models/BulkPatientManagerAssignmentResponseDTO'
import { PatientManagerAssignmentDTO } from '@om1/aspen-api/codegen/client/models/PatientManagerAssignmentDTO'
import { PatientManagerIndexDTO } from '@om1/aspen-api/codegen/client/models/PatientManagerIndexDTO'
import { PatientManagerAssignmentsService } from '@om1/aspen-api/codegen/client/services/PatientManagerAssignmentsService'
import { useSnackbar } from 'notistack'
import { useCallback, useState } from 'react'

interface AssignedPatientManager extends PatientManagerIndexDTO {
    patientId: string
}

export const useAssignedPatientManagers = () => {
    const [assignedPatientManagers, setAssignedPatientManagers] = useState<AssignedPatientManager[]>([])
    const [loading, setLoading] = useState<boolean>(false)
    const [error, setError] = useState<string | null>(null)
    const { enqueueSnackbar } = useSnackbar()

    // Initialize assignments from existing patient data
    const initializeFromPatients = useCallback((patients: any[], patientManagers: PatientManagerIndexDTO[]) => {
        console.log(t`Initializing assignments from patients:`, patients.length)
        const existingAssignments: AssignedPatientManager[] = []

        patients.forEach((patient) => {
            if (patient.patientManagerId) {
                const manager = patientManagers.find((pm) => pm.id === patient.patientManagerId)
                if (manager) {
                    existingAssignments.push({
                        patientId: patient.id,
                        ...manager
                    })
                }
            }
        })

        console.log(t`Found existing assignments:`, existingAssignments.length)
        setAssignedPatientManagers((prevManagers) => {
            // Only add if we don't already have assignments
            if (prevManagers.length === 0) {
                return existingAssignments
            }
            return prevManagers
        })
    }, [])

    // Get assigned patient manager for a specific patient
    const getAssignedManager = useCallback(
        (patientId: string): AssignedPatientManager | null => {
            return assignedPatientManagers.find((manager) => manager.patientId === patientId) || null
        },
        [assignedPatientManagers]
    )

    // Assign a patient manager to a patient/candidate with API call
    const assignManager = useCallback(
        async (entityId: string, manager: PatientManagerIndexDTO | null, type: 'patient' | 'candidate' = 'patient') => {
            setLoading(true)
            setError(null)

            try {
                // Prepare API request
                const assignmentData: PatientManagerAssignmentDTO = {
                    id: entityId,
                    patientManagerId: manager?.id || null,
                    type: type
                }

                // Call API to assign/unassign patient manager
                const response = await PatientManagerAssignmentsService.assignPatientManagerPatientManagerAssignmentsPost({
                    requestBody: assignmentData
                })

                if (response.success) {
                    // Update local state only if API call succeeds
                    setAssignedPatientManagers((prevManagers) => {
                        // Remove any existing assignment for this entity
                        const filteredManagers = prevManagers.filter((m) => m.patientId !== entityId)

                        // Add new assignment if manager is provided
                        if (manager) {
                            const newAssignment: AssignedPatientManager = {
                                patientId: entityId,
                                ...manager
                            }
                            return [...filteredManagers, newAssignment]
                        }

                        // Return filtered array (assignment removed)
                        return filteredManagers
                    })

                    // Show success message
                    const message = manager
                        ? t`Patient manager "${manager.name}" assigned successfully`
                        : t`Patient manager assignment removed successfully`
                    enqueueSnackbar(message, { variant: 'success' })
                } else {
                    throw new Error(response.message || t`Failed to update patient manager assignment`)
                }
            } catch (err) {
                const errorMessage = err instanceof Error ? err.message : t`Failed to update patient manager assignment`
                setError(errorMessage)
                enqueueSnackbar(errorMessage, { variant: 'error' })
                console.error(t`Patient manager assignment error:`, err)
            } finally {
                setLoading(false)
            }
        },
        [enqueueSnackbar]
    )

    // Remove assignment for a patient/candidate (using assignManager with null)
    const unassignManager = useCallback(
        async (entityId: string, type: 'patient' | 'candidate' = 'patient') => {
            await assignManager(entityId, null, type)
        },
        [assignManager]
    )

    // Bulk assign a patient manager to multiple patients/candidates
    const bulkAssignManager = useCallback(
        async (
            entityIds: string[],
            manager: PatientManagerIndexDTO | null,
            type: 'patient' | 'candidate' = 'patient'
        ): Promise<BulkPatientManagerAssignmentResponseDTO | null> => {
            if (entityIds.length === 0) {
                return null
            }

            setLoading(true)
            setError(null)

            try {
                // Prepare API request
                const bulkAssignmentData: BulkPatientManagerAssignmentDTO = {
                    ids: entityIds,
                    type: type,
                    patientManagerId: manager?.id || null
                }

                // Call bulk API
                const response = await PatientManagerAssignmentsService.bulkAssignPatientManagerPatientManagerAssignmentsBulkPost({
                    requestBody: bulkAssignmentData
                })

                // Update local state for successful assignments
                const successfulIds = response.results.filter((r) => r.success).map((r) => r.id)

                if (successfulIds.length > 0) {
                    setAssignedPatientManagers((prevManagers) => {
                        // Remove any existing assignments for successful entities
                        const filteredManagers = prevManagers.filter((m) => !successfulIds.includes(m.patientId))

                        // Add new assignments if manager is provided
                        if (manager) {
                            const newAssignments: AssignedPatientManager[] = successfulIds.map((id) => ({
                                patientId: id,
                                ...manager
                            }))
                            return [...filteredManagers, ...newAssignments]
                        }

                        return filteredManagers
                    })
                }

                // Show appropriate message
                if (response.totalFailed === 0) {
                    const action = manager ? 'assigned' : 'removed'
                    enqueueSnackbar(t`Successfully ${action} patient manager for ${response.totalSuccessful} items`, {
                        variant: 'success'
                    })
                } else if (response.totalSuccessful > 0) {
                    enqueueSnackbar(t`Partially completed: ${response.totalSuccessful} succeeded, ${response.totalFailed} failed`, {
                        variant: 'warning'
                    })
                } else {
                    enqueueSnackbar(t`Failed to assign patient manager to all items`, { variant: 'error' })
                }

                return response
            } catch (err) {
                const errorMessage = err instanceof Error ? err.message : t`Failed to bulk assign patient manager`
                setError(errorMessage)
                enqueueSnackbar(errorMessage, { variant: 'error' })
                console.error('Bulk patient manager assignment error:', err)
                return null
            } finally {
                setLoading(false)
            }
        },
        [enqueueSnackbar]
    )

    // Clear all assignments
    const clearAllAssignments = useCallback(() => {
        setAssignedPatientManagers([])
    }, [])

    // Get all assigned managers
    const getAllAssignments = useCallback(() => {
        return assignedPatientManagers
    }, [assignedPatientManagers])

    return {
        assignedPatientManagers,
        loading,
        error,
        getAssignedManager,
        assignManager,
        bulkAssignManager,
        unassignManager,
        clearAllAssignments,
        getAllAssignments,
        initializeFromPatients
    }
}
